import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown,
  UserPlus, UserMinus, CreditCard, Download, Users,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// THEME TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const t = {
  bg:              '#0d0e17',
  sidebar:         '#10111c',
  card:            '#181929',
  cardHover:       '#1d1e30',
  cardBorder:      'rgba(255,255,255,0.07)',
  cardBorderHover: 'rgba(255,255,255,0.14)',
  text:            '#ffffff',
  textMuted:       '#8b8fa8',
  textFaint:       '#4b4f6a',
  accent:          '#22d3b4',
  accentDim:       'rgba(34,211,180,0.10)',
  accentBorder:    'rgba(34,211,180,0.35)',
  green:           '#10b981',
  red:             '#f43f5e',
  amber:           '#f59e0b',
  blue:            '#3b82f6',
  purple:          '#a855f7',
};

// ─────────────────────────────────────────────────────────────────────────────
// REUSABLE: CARD WRAPPER
// ─────────────────────────────────────────────────────────────────────────────
const Card = ({ children, style = {}, className = '' }) => (
  <div className={`ad-card ${className}`} style={{
    background: t.card,
    border: `1px solid ${t.cardBorder}`,
    ...style,
  }}>
    {children}
  </div>
);

const CardHeader = ({ title, subtitle, action }) => (
  <div className="ad-card-header">
    <div style={{ minWidth: 0, flex: 1 }}>
      <h3 className="ad-card-title">{title}</h3>
      {subtitle && <p className="ad-card-subtitle">{subtitle}</p>}
    </div>
    {action && <div style={{ flexShrink: 0 }}>{action}</div>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// KPI CARD
// ─────────────────────────────────────────────────────────────────────────────
const KpiCard = ({ title, value, change, changeLabel, positive, icon: Icon, iconColor }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      className={`ad-kpi-card ${hov ? 'hov' : ''}`}
      style={{
        background: hov ? t.cardHover : t.card,
        border: `1px solid ${hov ? t.cardBorderHover : t.cardBorder}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: t.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {title}
        </span>
        <div style={{
          width: '34px', height: '34px', borderRadius: '10px',
          background: `${iconColor}1a`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: iconColor, flexShrink: 0,
        }}>
          <Icon size={17} />
        </div>
      </div>
      <div className="ad-kpi-val">
        {value}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: positive ? t.green : t.red, display: 'flex', alignItems: 'center', gap: '2px' }}>
          {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
          {change}
        </span>
        <span style={{ fontSize: '11px', color: t.textFaint }}>{changeLabel}</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// REVENUE AREA CHART (pure SVG — no library needed)
// ─────────────────────────────────────────────────────────────────────────────
const RevenueChart = ({ activeTab, setActiveTab }) => {
  const datasets = {
    Revenue:   [42, 55, 48, 60, 58, 72, 85, 90, 108, 115, 125, 150],
    Customers: [30, 38, 42, 50, 54, 60, 68, 72,  80,  85,  90,  95],
    Churn:     [ 8,  6,  9,  5,  7,  4,  6,  3,   5,   4,   3,   4],
  };
  const labels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const data   = datasets[activeTab];
  const maxVal = Math.max(...data);
  const W = 560, H = 175;
  const PAD = { top: 12, bottom: 22, left: 40, right: 8 };
  const xStep  = (W - PAD.left - PAD.right) / (data.length - 1);
  const yScale = v => PAD.top + (H - PAD.top - PAD.bottom) * (1 - v / (maxVal * 1.15));
  const pts    = data.map((v, i) => `${PAD.left + i * xStep},${yScale(v)}`).join(' ');
  const area   = `${PAD.left},${H - PAD.bottom} ${pts} ${PAD.left + (data.length - 1) * xStep},${H - PAD.bottom}`;
  const yTicks = [0, Math.round(maxVal * 0.33), Math.round(maxVal * 0.67), maxVal].reverse();
  const fmt    = v => activeTab === 'Revenue' ? `$${v}k` : activeTab === 'Customers' ? `${v}k` : `${v}%`;

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', flexWrap: 'wrap' }}>
        {['Revenue', 'Customers', 'Churn'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '5px 15px', borderRadius: '999px', border: 'none',
            fontSize: '12px', fontWeight: 700, cursor: 'pointer',
            background: activeTab === tab ? t.accent : 'rgba(255,255,255,0.06)',
            color: activeTab === tab ? '#000' : t.textMuted,
            transition: 'all 0.18s',
          }}>{tab}</button>
        ))}
      </div>
      <div className="ad-chart-svg-wrapper">
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', minWidth: '340px', height: '175px', overflow: 'visible', display: 'block' }}>
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={t.accent} stopOpacity="0.3" />
              <stop offset="100%" stopColor={t.accent} stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {yTicks.map((tick, i) => {
            const y = yScale(tick);
            return (
              <g key={i}>
                <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                  stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4,5" />
                <text x={PAD.left - 5} y={y + 3.5} textAnchor="end"
                  fill={t.textFaint} fontSize="8.5" fontFamily="Inter,sans-serif">
                  {fmt(tick)}
                </text>
              </g>
            );
          })}
          <polygon points={area} fill="url(#areaGrad)" />
          <polyline points={pts} fill="none" stroke={t.accent}
            strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
          {data.map((v, i) => (
            <circle key={i}
              cx={PAD.left + i * xStep} cy={yScale(v)} r="3.5"
              fill={t.accent} stroke={t.card} strokeWidth="1.5"
              opacity="0"
              onMouseEnter={e => { e.target.style.opacity = 1; }}
              onMouseLeave={e => { e.target.style.opacity = 0; }}
              style={{ cursor: 'crosshair', transition: 'opacity 0.15s' }}
            />
          ))}
          {labels.map((l, i) => (
            <text key={i} x={PAD.left + i * xStep} y={H - 5}
              textAnchor="middle" fill={t.textFaint} fontSize="8.5" fontFamily="Inter,sans-serif">
              {l}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MRR MOVEMENT
// ─────────────────────────────────────────────────────────────────────────────
const MrrMovement = () => {
  const items = [
    { label: 'New MRR',     value: '+$18.4k', color: t.green, dot: t.green },
    { label: 'Expansion',   value: '+$6.2k',  color: t.green, dot: t.blue  },
    { label: 'Contraction', value: '-$1.8k',  color: t.red,   dot: t.amber },
    { label: 'Churned',     value: '-$3.1k',  color: t.red,   dot: t.red   },
  ];
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '13px 0',
          borderBottom: i < items.length - 1 ? `1px solid ${t.cardBorder}` : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: item.dot, display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: t.textMuted }}>{item.label}</span>
          </div>
          <span style={{ fontSize: '13px', fontWeight: 700, color: item.color }}>{item.value}</span>
        </div>
      ))}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingTop: '16px', marginTop: '4px',
        borderTop: `1px solid ${t.cardBorder}`,
      }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Net MRR
        </span>
        <span style={{ fontSize: '26px', fontWeight: 900, color: t.text, letterSpacing: '-0.02em' }}>+$19.7k</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ONBOARDING FUNNEL
// ─────────────────────────────────────────────────────────────────────────────
const OnboardingFunnel = () => {
  const steps = [
    { label: 'Signed Up',          pct: 100, drop: null,            highlight: false },
    { label: 'Email Verified',     pct: 85,  drop: '-15% drop-off', highlight: false },
    { label: 'Profile Completed',  pct: 63,  drop: '-22% drop-off', highlight: false },
    { label: 'Workspace Created',  pct: 45,  drop: '-18% drop-off', highlight: false },
    { label: 'First Signal Seen',  pct: 40,  drop: '-5% drop-off',  highlight: true  },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {steps.map((step, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: step.highlight ? t.accent : t.text }}>
              {step.label}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: step.highlight ? t.accent : t.text }}>
              {step.pct}%
            </span>
          </div>
          <div style={{ height: '7px', background: 'rgba(255,255,255,0.06)', borderRadius: '999px', overflow: 'hidden', marginBottom: '4px' }}>
            <div style={{
              height: '100%', width: `${step.pct}%`,
              background: `linear-gradient(90deg, ${t.accent}, rgba(34,211,180,0.45))`,
              borderRadius: '999px',
              boxShadow: step.highlight ? `0 0 8px ${t.accent}55` : 'none',
              transition: 'width 1s ease',
            }} />
          </div>
          {step.drop && (
            <div style={{ textAlign: 'right', fontSize: '10.5px', color: t.textFaint }}>{step.drop}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AT-RISK CUSTOMERS — row extracted as component to safely use useState
// ─────────────────────────────────────────────────────────────────────────────
const riskBadge = score => {
  if (score >= 80) return { bg: 'rgba(244,63,94,0.13)',  color: '#f43f5e', border: 'rgba(244,63,94,0.28)',  label: 'Red'   };
  if (score >= 50) return { bg: 'rgba(245,158,11,0.13)', color: '#f59e0b', border: 'rgba(245,158,11,0.28)', label: 'Amber' };
  return                  { bg: 'rgba(16,185,129,0.13)', color: '#10b981', border: 'rgba(16,185,129,0.28)', label: 'Green' };
};

const AtRiskRow = ({ row, isLast }) => {
  const [hov, setHov] = useState(false);
  const badge = riskBadge(row.score);
  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${t.cardBorder}`,
        background: hov ? 'rgba(255,255,255,0.025)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <td style={{ padding: '13px 12px', fontSize: '13px', color: t.text, fontWeight: 500 }}>{row.email}</td>
      <td style={{ padding: '13px 12px', fontSize: '13px', color: t.textMuted }}>{row.reason}</td>
      <td style={{ padding: '13px 12px', textAlign: 'right' }}>
        <span style={{
          background: badge.bg, color: badge.color,
          padding: '4px 11px', borderRadius: '999px',
          fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
          border: `1px solid ${badge.border}`,
        }}>
          {row.score} / {badge.label}
        </span>
      </td>
    </tr>
  );
};

const AtRiskCustomers = () => {
  const rows = [
    { email: 'acme.corp@example.com', reason: 'Payment Failed (3x)',          score: 98 },
    { email: 'j.smith@starkind.com',  reason: 'No login in 45 days',          score: 85 },
    { email: 'tech.lead@wayne.io',    reason: 'Support tickets open > 7d',    score: 62 },
    { email: 'info@globex.co',        reason: 'Usage dropped 40%',            score: 41 },
    { email: 'contact@initech.com',   reason: 'Overdue invoices (2)',         score: 73 },
    { email: 'help@hooli.com',        reason: 'Feedback pending for 30 days', score: 55 },
  ];
  return (
    <div className="ad-table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
        <thead>
          <tr>
            {['Email', 'Reason', 'Risk Score'].map((h, i) => (
              <th key={h} style={{
                padding: '8px 12px',
                fontSize: '10px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.09em',
                color: t.textFaint, textAlign: i === 2 ? 'right' : 'left',
                borderBottom: `1px solid ${t.cardBorder}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <AtRiskRow key={i} row={row} isLast={i === rows.length - 1} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RECENT SIGNUPS — row extracted as component to safely use useState
// ─────────────────────────────────────────────────────────────────────────────
const planStyles = {
  Elite:   { bg: 'rgba(34,211,180,0.10)',  color: '#22d3b4', border: 'rgba(34,211,180,0.28)'  },
  Premium: { bg: 'rgba(168,85,247,0.10)',  color: '#a855f7', border: 'rgba(168,85,247,0.28)'  },
  Basic:   { bg: 'rgba(255,255,255,0.06)', color: '#8b8fa8', border: 'rgba(255,255,255,0.12)' },
};
const statusStyles = {
  Active:   { color: '#10b981', dot: '#10b981' },
  Pending:  { color: '#f59e0b', dot: '#f59e0b' },
  Inactive: { color: '#f43f5e', dot: '#f43f5e' },
};

const SignupRow = ({ row, isLast }) => {
  const [hov, setHov] = useState(false);
  const plan = planStyles[row.plan] || planStyles.Basic;
  const st   = statusStyles[row.status] || statusStyles.Active;
  return (
    <tr
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: isLast ? 'none' : `1px solid ${t.cardBorder}`,
        background: hov ? 'rgba(255,255,255,0.025)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <td style={{ padding: '15px 16px', fontSize: '13px', color: t.text, fontWeight: 500 }}>{row.email}</td>
      <td style={{ padding: '15px 16px', fontSize: '13px', color: t.textMuted, whiteSpace: 'nowrap' }}>{row.date}</td>
      <td style={{ padding: '15px 16px' }}>
        <span style={{
          background: plan.bg, color: plan.color,
          padding: '4px 11px', borderRadius: '6px',
          fontSize: '11px', fontWeight: 700,
          border: `1px solid ${plan.border}`,
        }}>{row.plan}</span>
      </td>
      <td style={{ padding: '15px 16px', fontSize: '13px', color: t.textMuted }}>{row.country}</td>
      <td style={{ padding: '15px 16px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: st.color, fontWeight: 600 }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: '50%',
            background: st.dot, display: 'inline-block', flexShrink: 0,
            boxShadow: `0 0 5px ${st.dot}88`,
          }} />
          {row.status}
        </span>
      </td>
    </tr>
  );
};

const RecentSignups = () => {
  const rows = [
    { email: 'sarah.j@techflow.io',   date: 'Today, 14:23',     plan: 'Elite',   country: 'United States',  status: 'Active'   },
    { email: 'dev@pixelmatters.com',   date: 'Today, 11:05',     plan: 'Premium', country: 'United Kingdom', status: 'Active'   },
    { email: 'm.scott@papercorp.net',  date: 'Yesterday, 16:40', plan: 'Basic',   country: 'Canada',         status: 'Pending'  },
    { email: 'hannah.w@designsys.co', date: 'Yesterday, 09:12', plan: 'Elite',   country: 'Germany',        status: 'Active'   },
    { email: 'r.kim@infrastack.dev',   date: 'May 1, 20:55',     plan: 'Premium', country: 'South Korea',    status: 'Active'   },
    { email: 'lena.m@brandcraft.io',   date: 'May 1, 17:30',     plan: 'Basic',   country: 'France',         status: 'Inactive' },
  ];
  return (
    <div className="ad-table-scroll">
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
        <thead>
          <tr>
            {['Email', 'Signup Date', 'Plan', 'Country', 'Status'].map(h => (
              <th key={h} style={{
                padding: '8px 16px',
                fontSize: '10px', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.09em',
                color: t.textFaint, textAlign: 'left',
                borderBottom: `1px solid ${t.cardBorder}`,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <SignupRow key={i} row={row} isLast={i === rows.length - 1} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ROOT: AdminDashboard
// ─────────────────────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('Revenue');

  return (
    <div className="ad-wrapper">
      <style>{`
        .ad-wrapper {
          display: flex;
          min-height: 100vh;
          background: #0d0e17;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #ffffff;
          font-size: 14px;
          width: 100%;
          max-width: 100vw;
          min-width: 0;
          overflow-x: hidden;
          box-sizing: border-box;
        }
        .ad-wrapper *, .ad-wrapper *::before, .ad-wrapper *::after {
          box-sizing: border-box;
        }
        .ad-scroll-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          overflow-y: auto;
        }
        .ad-main-content {
          padding: 24px 28px 60px 28px;
          display: flex;
          flex-direction: column;
          gap: 20px;
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .ad-grid-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          width: 100%;
          max-width: 100%;
          min-width: 0;
        }
        .ad-card {
          background: #181929;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px;
          padding: 24px;
          transition: all 0.2s;
          box-sizing: border-box;
          min-width: 0;
          max-width: 100%;
          overflow: hidden;
        }
        .ad-card-chart {
          flex: 2 1 400px;
        }
        .ad-card-mrr {
          flex: 1 1 240px;
        }
        .ad-card-funnel {
          flex: 1 1 260px;
        }
        .ad-card-at-risk {
          flex: 1.6 1 320px;
        }
        .ad-card-signups {
          width: 100%;
          flex: 1 1 100%;
        }
        .ad-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
          width: 100%;
          max-width: 100%;
        }
        .ad-card-title {
          font-size: 15px;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 3px;
        }
        .ad-card-subtitle {
          font-size: 12px;
          color: #8b8fa8;
          margin: 0;
        }
        .ad-kpi-card {
          background: #181929;
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 16px;
          padding: 20px 22px;
          flex: 1 1 200px;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
          transition: all 0.2s;
          cursor: default;
        }
        .ad-kpi-card.hov {
          background: #1d1e30;
          border-color: rgba(255,255,255,0.14);
        }
        .ad-kpi-val {
          font-size: 30px;
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 10px;
          letter-spacing: -0.02em;
          line-height: 1.1;
          word-break: break-word;
        }
        .ad-table-scroll {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }
        .ad-chart-svg-wrapper {
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }

        @media (max-width: 860px) {
          .ad-main-content {
            padding: 16px 12px 60px 12px;
            gap: 16px;
          }
          .ad-card {
            padding: 18px 14px;
            border-radius: 14px;
            width: 100% !important;
            flex: 1 1 100% !important;
          }
          .ad-card-chart,
          .ad-card-mrr,
          .ad-card-funnel,
          .ad-card-at-risk,
          .ad-card-signups {
            flex: 1 1 100% !important;
            width: 100% !important;
          }
          .ad-kpi-card {
            padding: 16px 14px;
            border-radius: 12px;
            flex: 1 1 calc(50% - 8px);
          }
          .ad-kpi-val {
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .ad-main-content {
            padding: 12px 8px 60px 8px;
            gap: 12px;
          }
          .ad-card {
            padding: 14px 10px;
            border-radius: 12px;
          }
          .ad-kpi-card {
            flex: 1 1 100%;
            padding: 14px 12px;
          }
          .ad-kpi-val {
            font-size: 24px;
          }
          .ad-card-header {
            margin-bottom: 14px;
          }
        }
      `}</style>

      {/* ══════════════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════════════ */}
      <div className="ad-scroll-container">

        {/* Page Content */}
        <main className="ad-main-content">

          {/* ① KPI Row */}
          <div className="ad-grid-row">
            <KpiCard title="MRR"                value="$124,592" change="+12.5%" changeLabel="Driven by new signups"  positive         icon={CreditCard}  iconColor={t.accent}  />
            <KpiCard title="Total Customers"    value="8,430"    change="+3.2%"  changeLabel="Active subscriptions"   positive         icon={Users}       iconColor={t.purple}  />
            <KpiCard title="New This Month"     value="342"      change="+8.1%"  changeLabel="Vs. last month"         positive         icon={UserPlus}    iconColor={t.blue}    />
            <KpiCard title="Churned This Month" value="27"       change="-2.1%"  changeLabel="Churn stabilizing"      positive={false} icon={UserMinus}   iconColor={t.amber}   />
          </div>

          {/* ② Revenue Trend + MRR Movement */}
          <div className="ad-grid-row">
            <Card className="ad-card-chart">
              <CardHeader title="Revenue Trend" subtitle="Monthly recurring revenue over time" />
              <RevenueChart activeTab={activeTab} setActiveTab={setActiveTab} />
            </Card>
            <Card className="ad-card-mrr">
              <CardHeader title="MRR Movement" subtitle="Current month breakdown" />
              <MrrMovement />
            </Card>
          </div>

          {/* ③ Onboarding Funnel + At-Risk Customers */}
          <div className="ad-grid-row">
            <Card className="ad-card-funnel">
              <CardHeader title="Onboarding Funnel" subtitle="Conversion rates across stages" />
              <OnboardingFunnel />
            </Card>
            <Card className="ad-card-at-risk">
              <CardHeader
                title="At-Risk Customers"
                subtitle="High churn probability"
                action={
                  <button style={{ background: 'none', border: 'none', fontSize: '12px', color: t.accent, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                    View All
                  </button>
                }
              />
              <AtRiskCustomers />
            </Card>
          </div>

          {/* ④ Recent Signups */}
          <Card className="ad-card-signups">
            <CardHeader
              title="Recent Signups"
              subtitle="Latest user registrations"
              action={
                <button style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: 'rgba(255,255,255,0.05)',
                  border: `1px solid ${t.cardBorder}`,
                  borderRadius: '8px', padding: '7px 14px',
                  color: t.textMuted, fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                }}>
                  <Download size={13} /> Export CSV
                </button>
              }
            />
            <RecentSignups />
          </Card>

        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;